/**
 * src/pages/Pricing.jsx
 * Tabla de precios pública — accesible sin login.
 *
 * Los alcances de cada plan replican la tabla v2 cerrada el 17-07-2026; la
 * fuente de verdad del enforcement vive en app/services/tier_limits.py (editar
 * ambos a la vez si cambian los topes).
 *
 * Enterprise es venta asistida: no pasa por checkout, abre contacto comercial.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { crearCheckout } from '../api/billing'

const EMAIL_COMERCIAL = 'contacto@cmmonitor.cl'

// Precio mensual en UF. El anual regala 2 meses: se paga 10, se usan 12.
const MESES_ANUAL = 10

const PLANES = [
  {
    id: 'basic', nombre: 'Basic', precio: 1.25,
    desc: 'Para el especialista: monitorea tu catálogo a diario.',
    features: [
      '1 empresa propia',
      'Hasta 4 familias/convenios en Convenio Marco',
      'Monitoreo para catálogos de hasta ~1.000 productos',
      'Actualización de precios diaria',
      'Comparación vs. 5 competidores',
      'Alertas por email + resumen mensual',
      'Historial de datos de 180 días',
    ],
    featured: false,
  },
  {
    id: 'pro', nombre: 'Pro', precio: 2.5,
    desc: 'Para operar en serio: competencia y simulador.',
    heredaDe: 'Basic',
    features: [
      'Módulo Competencia (competidores nombrados)',
      'Simulador de precios para posicionar productos',
      '2 empresas propias · 6 familias · catálogos de ~2.500 productos',
      'Alertas por email y Telegram',
      'Historial de 12 meses · 2 usuarios del panel',
    ],
    featured: true,
  },
  {
    id: 'premium', nombre: 'Premium', precio: 5.5,
    desc: 'Catálogo amplio: predicción y todas tus familias.',
    heredaDe: 'Pro',
    features: [
      'Predictor de oportunidades de compra (CM, CA, TD…)',
      'Todas tus familias/convenios, sin límite',
      '5 empresas propias · catálogos de ~5.000 productos',
      'Historial de 24 meses · 5 usuarios del panel',
      'Soporte técnico prioritario',
    ],
    featured: false,
  },
  {
    id: 'enterprise', nombre: 'Enterprise', precio: 9, desde: true,
    desc: 'Gran catálogo: sin límites y acompañamiento.',
    heredaDe: 'Premium',
    features: [
      'Catálogos sobre 5.000 productos, sin techo',
      'Alertas por WhatsApp · 10 usuarios del panel',
      'Onboarding dedicado e informe ejecutivo',
      'Plan y precio a medida',
    ],
    featured: false,
  },
]

/** 1.25 → "1,25" · 2.5 → "2,5" · 25 → "25" */
function fmt(n) {
  return n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')
}

export default function Pricing() {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState('')
  const [anual, setAnual] = useState(false)

  async function handlePlan(tier) {
    setError('')
    if (tier === 'enterprise') {
      window.location.href =
        `mailto:${EMAIL_COMERCIAL}?subject=${encodeURIComponent('Consulta plan Enterprise')}`
      return
    }
    const ciclo = anual ? 'anual' : 'mensual'
    if (!isAuthenticated) {
      navigate(`/register?tier=${tier}&ciclo=${ciclo}`)
      return
    }
    setLoading(tier)
    try {
      const { url } = await crearCheckout(tier, ciclo)
      window.location.href = url
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al iniciar el pago')
    } finally { setLoading(null) }
  }

  const btnCiclo = (activo) => ({
    padding: '7px 18px', borderRadius: '999px', border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-ui)', fontSize: '13px', fontWeight: 700,
    background: activo ? 'var(--color-surface)' : 'transparent',
    color: activo ? 'var(--color-text)' : 'var(--color-text-2)',
    boxShadow: activo ? '0 1px 3px rgba(30,35,42,.10)' : 'none',
  })

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', padding: '60px 24px' }}>
      <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '38px' }}>
          <div style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--color-accent)', letterSpacing: '0.1em', marginBottom: '12px' }}>PRECIOS</div>
          <h1 style={{ fontSize: '36px', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '12px' }}>Simple y transparente</h1>
          <p style={{ color: 'var(--color-text-2)', fontSize: '16px' }}>Precios especiales de lanzamiento · 14 días de prueba gratis</p>

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '24px', background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: '999px', padding: '4px' }}>
            <button onClick={() => setAnual(false)} style={btnCiclo(!anual)}>Mensual</button>
            <button onClick={() => setAnual(true)} style={btnCiclo(anual)}>Anual</button>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10.5px', color: 'var(--color-success, #1F9E6E)', padding: '0 10px 0 6px', whiteSpace: 'nowrap' }}>
              2 meses gratis
            </span>
          </div>
        </div>

        {error && <div style={{ textAlign: 'center', color: 'var(--color-error)', fontFamily: 'var(--font-mono)', fontSize: '13px', marginBottom: '20px' }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px', alignItems: 'stretch' }}>
          {PLANES.map(plan => {
            const mensual = plan.precio
            const porMes = anual ? (mensual * MESES_ANUAL) / 12 : mensual
            const totalAnual = mensual * MESES_ANUAL
            return (
              <div key={plan.id} style={{
                background: 'var(--color-surface)',
                border: `1px solid ${plan.featured ? 'var(--color-accent)' : 'var(--color-border)'}`,
                borderRadius: '14px', padding: '28px 24px',
                display: 'flex', flexDirection: 'column',
                position: 'relative',
              }}>
                {plan.featured && (
                  <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--color-accent)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 14px', borderRadius: '999px', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                    Más elegido
                  </div>
                )}
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--color-text-3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>{plan.nombre}</div>

                <div style={{ fontSize: '32px', fontWeight: 700, fontFamily: 'var(--font-mono)', letterSpacing: '-0.03em', marginBottom: '2px' }}>
                  {plan.desde && <span style={{ fontSize: '14px', fontWeight: 400, color: 'var(--color-text-2)', marginRight: '6px' }}>desde</span>}
                  {fmt(porMes)}<span style={{ fontSize: '14px', color: 'var(--color-text-3)', fontWeight: 400 }}> UF/mes</span>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--color-text-3)', minHeight: '18px' }}>
                  {plan.id === 'enterprise'
                    ? 'Precio a medida'
                    : anual ? `${fmt(totalAnual)} UF/año · facturación anual` : 'Facturación mensual'}
                </div>

                <p style={{ fontSize: '13px', color: 'var(--color-text-2)', margin: '12px 0 16px' }}>{plan.desc}</p>
                <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '0 0 16px' }} />

                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, marginBottom: '22px', padding: 0 }}>
                  {plan.heredaDe && (
                    <li style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text)', marginBottom: '2px' }}>
                      Todo lo de {plan.heredaDe}, y además:
                    </li>
                  )}
                  {plan.features.map(f => (
                    <li key={f} style={{ fontSize: '13px', color: 'var(--color-text-2)', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <span style={{ color: 'var(--color-accent)', fontSize: '12px', flexShrink: 0, marginTop: '2px' }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePlan(plan.id)}
                  disabled={loading === plan.id}
                  style={{
                    width: '100%', padding: '11px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)',
                    background: plan.featured ? 'var(--color-accent)' : 'var(--color-surface-2)',
                    color: plan.featured ? '#fff' : 'var(--color-text)',
                    border: plan.featured ? 'none' : '1px solid var(--color-border)',
                    opacity: loading === plan.id ? 0.7 : 1,
                  }}
                >
                  {loading === plan.id
                    ? 'Redirigiendo...'
                    : plan.id === 'enterprise' ? 'Hablar con ventas' : 'Empezar prueba gratis'}
                </button>
              </div>
            )
          })}
        </div>

        <p style={{ textAlign: 'center', marginTop: '26px', fontSize: '13px', color: 'var(--color-text-2)' }}>
          Durante los <strong>14 días de prueba</strong> se desbloquean <strong>Módulo Competencia</strong> y <strong>Predictor</strong> en cualquier plan.
        </p>
        <p style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px', color: 'var(--color-text-3)' }}>
          Precios en UF · IVA no incluido · Flow convierte a CLP al valor del día · Cancela en cualquier momento
        </p>
      </div>
    </div>
  )
}
